import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getCourseBySlug } from "@/lib/courses";
import { Button } from "@/components/ui/button";
import CourseHeader from "@/components/course-detail/CourseHeader";
import CourseMeta from "@/components/course-detail/CourseMeta";
import CourseImageBlock from "@/components/course-detail/CourseImageBlock";
import CourseSections from "@/components/course-detail/CourseSections";
import CourseFormat from "@/components/course-detail/CourseFormat";
import CourseDescription from "@/components/course-detail/CourseDescription";
import CourseInstructorSection from "@/components/course-detail/CourseInstructorSection";
import CourseDetailLoading from "@/components/course-detail/CourseDetailLoading";
import CourseDetailError from "@/components/course-detail/CourseDetailError";
import { useAuth } from "@/contexts/AuthContext";
import { getUserCourses } from "@/lib/user-courses";
import React from "react";

const CourseDetailPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const {
    data: course,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["course-detail", slug],
    queryFn: async () => {
      if (!slug) return null;
      return await getCourseBySlug(slug);
    },
    enabled: !!slug,
  });

  // Handle purchase click with enrolled check
  const handlePurchaseClick = async () => {
    if (!course) return;

    if (user && course.id) {
      const courses = await getUserCourses(user.id);
      const found = courses.find(c => c.course_id === course.id);
      if (found) {
        navigate(`/learner/course/${course.id}`);
        return;
      }
    }
    navigate(`/checkout/${course.id}`);
  };

  if (isLoading) {
    return <CourseDetailLoading />;
  }

  if (error || !course) {
    return <CourseDetailError />;
  }

  const {
    id: courseId,
    title,
    image_url,
    price,
    currency = "EUR",
    description,
    short_description,
    lesson_count,
    duration,
    level,
    format_description,
    learning_outcomes,
    target_audience,
  } = course;

  return (
    <div className="bg-kaija-light/60 min-h-screen py-8 fade-in">
      <div className="container-custom max-w-5xl">
        <div className="flex flex-col md:flex-row md:gap-12 gap-6 items-start mb-12">
          <CourseImageBlock
            image_url={image_url}
            title={title}
            price={price}
            currency={currency}
          />
          <div className="flex-1 min-w-0">
            <CourseHeader title={title} />
            <CourseMeta level={level} duration={duration} />
            <p className="text-gray-700 mb-5">{short_description || description}</p>
            <div className="flex items-center mb-3 mt-2">
              <span className="text-2xl font-semibold text-kaija-accent">{price}&nbsp;{currency}</span>
            </div>
            <Button
              className="bg-kaija-accent hover:bg-kaija-accent/90 text-white text-base px-8 py-4 mt-2 font-bold shadow"
              onClick={handlePurchaseClick}
            >
              Osta kursus
            </Button>
          </div>
        </div>
        <CourseSections
          learning_outcomes={learning_outcomes}
          target_audience={target_audience}
        />
        <CourseFormat format_description={format_description} />
        <CourseDescription description={description} />
        <CourseInstructorSection />
      </div>
    </div>
  );
};

export default CourseDetailPage;
